import type { BookingWizardState } from "./useBookingDraft.js";
import type { PriceBreakdown } from "../../shared/types.js";
import { BUILDING_TYPE_LABELS } from "../../shared/types.js";
import { ValidationMessage } from "../../components/States.js";

function formatEuro(cents: number): string {
  return (cents / 100).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function BookingReview({
  state,
  breakdown,
  onUpdate,
  submitError,
}: {
  state: BookingWizardState;
  breakdown: PriceBreakdown | null;
  onUpdate: (patch: Partial<BookingWizardState>) => void;
  submitError?: string | null;
}) {
  const { building, service, contact, slot } = state;
  return (
    <div>
      <h2>Controleer en bevestig</h2>
      <p className="text-small text-muted">Controleer uw gegevens hieronder en bevestig uw boeking.</p>

      <div className="review-block">
        <dl>
          <dt>Gebouw</dt>
          <dd>
            {BUILDING_TYPE_LABELS[building.buildingType]}, {building.floors} bouwlagen, {building.surfaceM2} m²,
            bouwjaar {building.buildYear}
          </dd>
          <dt>Keuringen</dt>
          <dd>
            Asbestattest
            {service.addOns.includes("epc") ? " + EPC" : ""}
            {service.addOns.includes("elektrische_keuring") ? " + elektrische keuring" : ""}
            {service.extraSamples > 0 ? ` + ${service.extraSamples} extra staalname(s)` : ""}
            {service.urgent ? " + spoedafspraak" : ""}
          </dd>
          <dt>Adres</dt>
          <dd>
            {contact.street} {contact.houseNumber}
            {contact.bus ? `/${contact.bus}` : ""}, {contact.postalCode} {contact.city}
          </dd>
          <dt>Contact</dt>
          <dd>
            {contact.firstName} {contact.lastName} — {contact.email} — {contact.phone}
          </dd>
          <dt>Datum &amp; tijd</dt>
          <dd>{slot ? `${slot.date} om ${slot.time}` : "Nog niet gekozen"}</dd>
        </dl>
      </div>

      {breakdown && breakdown.attestNotRequired && (
        <div className="card card-pad mt-4">
          <p className="mb-0 text-small">
            <strong>Geen asbestattest nodig.</strong> {breakdown.attestNotRequiredReason} Deze boeking kan daarom
            niet bevestigd worden.
          </p>
        </div>
      )}

      {breakdown && !breakdown.attestNotRequired && !breakdown.requiresCustomQuote && (
        <div className="card card-pad mt-4">
          <h3>Prijsoverzicht</h3>
          {breakdown.lineItems.map((li) => (
            <div className="price-summary__line" key={li.id}>
              <span>{li.label}</span>
              <span>&euro;{formatEuro(li.amountExclVat)}</span>
            </div>
          ))}
          <div className="price-summary__line">
            <span>Btw ({breakdown.vatRatePercent}%)</span>
            <span>&euro;{formatEuro(breakdown.vatCents)}</span>
          </div>
          {breakdown.contributionCents !== 0 && (
            <div className="price-summary__line">
              <span>{breakdown.contributionLabel}</span>
              <span>
                {breakdown.contributionCents < 0 ? "-" : "+"}&euro;{formatEuro(Math.abs(breakdown.contributionCents))}
              </span>
            </div>
          )}
          <div className="price-summary__total">
            <span>Totaal incl. btw</span>
            <span className="price-summary__total-value">&euro;{formatEuro(breakdown.totalInclVatCents)}</span>
          </div>
        </div>
      )}

      <div className="mt-6">
        <div className="checkbox-field">
          <input
            type="checkbox"
            id="acceptedTerms"
            checked={state.acceptedTerms}
            onChange={(e: any) => onUpdate({ acceptedTerms: e.target.checked })}
          />
          <label htmlFor="acceptedTerms">
            Ik bevestig mijn afspraak en ga akkoord met de{" "}
            <a href="/algemene-voorwaarden" target="_blank" rel="noopener noreferrer">
              algemene voorwaarden
            </a>{" "}
            (incl. annuleringsbeleid). *
          </label>
        </div>
        <div className="checkbox-field">
          <input
            type="checkbox"
            id="acceptedPrivacy"
            checked={state.acceptedPrivacy}
            onChange={(e: any) => onUpdate({ acceptedPrivacy: e.target.checked })}
          />
          <label htmlFor="acceptedPrivacy">
            Ik ga akkoord met de verwerking van mijn gegevens volgens de{" "}
            <a href="/privacybeleid" target="_blank" rel="noopener noreferrer">
              privacyverklaring
            </a>
            . *
          </label>
        </div>
        <div className="checkbox-field">
          <input
            type="checkbox"
            id="marketingConsent"
            checked={state.marketingConsent}
            onChange={(e: any) => onUpdate({ marketingConsent: e.target.checked })}
          />
          <label htmlFor="marketingConsent">
            Ik ontvang graag af en toe nieuws en tips over asbest en regelgeving (optioneel, u kunt zich altijd
            uitschrijven).
          </label>
        </div>
      </div>

      <ValidationMessage>{submitError}</ValidationMessage>
    </div>
  );
}
